import { parseConditions, isCriterionComplete } from './conditionModel';

const TRUTHY = new Set(['y', 'yes', 't', 'true', '1']);
const FALSY = new Set(['n', 'no', 'f', 'false', '0']);
const part = (text, bold = false) => ({ text, bold });
const plainOf = (parts) => parts.map((partEntry) => partEntry.text).join('');
const flip = (boolLabel) => (boolLabel === 'Yes' ? 'No' : 'Yes');
const attrNameOf = (attributeById, id) => (attributeById(id) || { name: `Attribute ${id}` }).name;

// Collapse a value array: case-fold keeping an uppercased variant when present
// (["A","a"]→"A"); detect all-truthy / all-falsy synonym sets.
export function reduceValues(values) {
  const strs = (values || []).map(String);
  const byLower = new Map();
  for (const value of strs) {
    const lowerKey = value.toLowerCase();
    if (!byLower.has(lowerKey) || (/[A-Z]/.test(value) && !/[A-Z]/.test(byLower.get(lowerKey)))) byLower.set(lowerKey, value);
  }
  const distinct = [...byLower.values()];
  const low = distinct.map((distinctValue) => distinctValue.toLowerCase());
  if (low.length && distinct.length > 1 && low.every((lowerValue) => TRUTHY.has(lowerValue))) return { kind: 'bool', bool: 'Yes' };
  if (low.length && distinct.length > 1 && low.every((lowerValue) => FALSY.has(lowerValue))) return { kind: 'bool', bool: 'No' };
  if (distinct.length <= 1) return { kind: 'one', value: distinct[0] || '' };
  return { kind: 'many', list: distinct.join(', ') };
}

// PARSED criterion → { plain, parts, kind }. `kind` drives who/whose in the summary.
export function humanizeCriterion(criterion, { attributeById, productById }) {
  if (criterion.type === 'product') {
    const names = (criterion.productIds || []).map((id) => (productById(id) || { name: `#${id}` }).name).join(', ');
    const verb = criterion.operator === 'EXCLUSIVE' ? "doesn't have" : 'has';
    const parts = [part(`${verb} `), part(names, true)];
    return { plain: plainOf(parts), parts, kind: 'verb' };
  }
  const name = attrNameOf(attributeById, criterion.attributeId);
  const reduced = reduceValues(criterion.values);
  const val = reduced.kind === 'bool' ? reduced.bool : reduced.kind === 'one' ? reduced.value : reduced.list;
  // "{name} is {…}" with negation, for the three value kinds
  const isf = (neg) => {
    const mid = reduced.kind === 'many' ? (neg ? ' is none of ' : ' is one of ')
      : reduced.kind === 'bool' ? ' is '
      : (neg ? ' is not ' : ' is ');
    const shown = reduced.kind === 'bool' ? (neg ? flip(reduced.bool) : reduced.bool) : val;
    return [part(name, true), part(mid), part(shown, true)];
  };
  const simple = (mid) => [part(name, true), part(` ${mid} `), part(val, true)];
  let parts;
  switch (criterion.operator) {
    case 'CONTAINS': parts = reduced.kind === 'bool' ? isf(false) : simple('contains'); break;
    case 'DOES_NOT_CONTAIN': parts = reduced.kind === 'bool' ? isf(true) : simple("doesn't contain"); break;
    case 'EQUAL': case 'IS': case 'INCLUSIVE': parts = isf(false); break;
    case 'NOT_EQUAL': case 'IS_NOT': case 'EXCLUSIVE': parts = isf(true); break;
    case 'GREATER_THAN': parts = simple('is more than'); break;
    case 'GREATER_THAN_OR_EQUAL': parts = simple('is at least'); break;
    case 'LESS_THAN': parts = simple('is less than'); break;
    case 'LESS_THAN_OR_EQUAL': parts = simple('is at most'); break;
    case 'AFTER': parts = simple('is after'); break;
    case 'BEFORE': parts = simple('is before'); break;
    case 'ON': parts = simple('is on'); break;
    case 'HAS_VALUE': parts = [part('has any '), part(name, true)]; break;
    case 'HAS_NO_VALUE': parts = [part('has no '), part(name, true)]; break;
    default: parts = simple(criterion.operator.toLowerCase().replace(/_/g, ' ')); break;
  }
  return { plain: plainOf(parts), parts, kind: (criterion.operator === 'HAS_VALUE' || criterion.operator === 'HAS_NO_VALUE') ? 'verb' : 'state' };
}

// "A", "A or B", "A, B, or C" — bold each name, plain separators. `word` = 'and' | 'or'.
function joinParts(names, word) {
  const out = [];
  names.forEach((name, i) => {
    if (i > 0) out.push(part(names.length === 2 ? ` ${word} ` : (i === names.length - 1 ? `, ${word} ` : ', ')));
    out.push(part(name, true));
  });
  return out;
}

export function completeCriteria(group) {
  const list = group.criteria.filter(isCriterionComplete);
  const constrained = new Set(list.filter((criterion) => criterion.type === 'attribute' && criterion.operator !== 'HAS_VALUE').map((criterion) => criterion.attributeId));
  return list.filter((criterion) => !(criterion.operator === 'HAS_VALUE' && constrained.has(criterion.attributeId)));
}

export function groupClauses(group, resolvers) {
  const word = group.criteriaOp === 'OR' ? 'or' : 'and';
  const crits = completeCriteria(group);
  const namesOf = (criterion) => (criterion.productIds || []).map((id) => (resolvers.productById(id) || { name: `#${id}` }).name);
  const emitted = new Set();
  const out = [];
  crits.forEach((criterion) => {
    if (criterion.type === 'product') {
      if (emitted.has(criterion.operator)) return;
      emitted.add(criterion.operator);
      const all = crits.filter((otherCriterion) => otherCriterion.type === 'product' && otherCriterion.operator === criterion.operator).flatMap(namesOf);
      const verb = criterion.operator === 'EXCLUSIVE' ? "doesn't have" : 'has';
      const parts = [part(`${verb} `), ...joinParts(all, word)];
      out.push({ plain: plainOf(parts), parts, kind: 'verb' });
    } else {
      out.push(humanizeCriterion(criterion, resolvers));
    }
  });
  return out;
}

export function buildSummary(groups, conditionOperator, resolvers) {
  const builtGroups = groups
    .map((group) => ({ op: group.criteriaOp === 'OR' ? 'any' : 'all', clauses: groupClauses(group, resolvers) }))
    .filter((group) => group.clauses.length);
  return { outer: conditionOperator === 'OR' ? 'any' : 'all', groups: builtGroups, clauseCount: builtGroups.reduce((count, group) => count + group.clauses.length, 0) };
}

export function summaryFromSegment(segment, resolvers) {
  if (!segment) return { outer: 'all', groups: [], clauseCount: 0 };
  return buildSummary(parseConditions(segment.condition), segment.condition_operator, resolvers);
}
